import { useState } from 'react';
import { api } from '@/api/client';
import { BillingCard } from './BillingCard';
import { InvoiceModal } from './InvoiceModal';
import { downloadInvoice } from './invoiceDocument';
import { DownloadIcon } from './DownloadIcon';
import { BillingPlansView } from './BillingPlansView';
import { useBillingData } from './useBillingData';
import type { Invoice, PaymentMethod, Plan, Subscription } from './types';
import { formatDate, formatMoney, renewalDate, TIMEZONE_NOTE } from './dateFormat';
import { getDocsUrl } from '../SettingsModal';
import './billing.css';

/**
 * Settings → Billing tab. Composes four cards over the live billing endpoints
 * (useBillingData): Plan overview, Payment method, Billing history and a
 * Cancellation danger zone. Each card loads/fails independently.
 *
 * Plan changes happen in-modal: "Adjust plan", "View plans" and "Update card"
 * swap the cards for an in-modal plans grid (BillingPlansView) — no navigation
 * to a separate page.
 */

interface SettingsBillingTabProps {
  /** Whether the Billing tab is currently shown — gates data fetching. */
  active: boolean;
}

const HISTORY_PREVIEW = 5;

const STATUS_LABEL: Record<Invoice['status'], string> = {
  SUCCESS: 'Paid',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
};

/** `price` is the charged amount straight from the DB (already discounted). */
function priceLabel(plan: Plan): string {
  if (plan.price === '0' || Number(plan.price) === 0) return 'Free';
  return `${formatMoney(Number(plan.price), plan.currency)} / ${plan.billingCycle.toLowerCase()}`;
}

export function SettingsBillingTab({ active }: SettingsBillingTabProps) {
  const { subscription, invoices, paymentMethod, refetchAll } = useBillingData(active);

  const [viewInvoice, setViewInvoice] = useState<Invoice | null>(null);
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [showPlans, setShowPlans] = useState(false);

  const openPlans = () => setShowPlans(true);

  if (showPlans) {
    return (
      <div className="billing-tab">
        <BillingPlansView
          subscription={subscription.data ?? null}
          onBack={() => setShowPlans(false)}
          onChanged={refetchAll}
        />
      </div>
    );
  }

  return (
    <div className="billing-tab">
      <PlanOverviewCard
        subscription={subscription.data}
        subLoading={subscription.loading}
        subError={subscription.error}
        onRetrySub={subscription.refetch}
        onAdjustPlan={openPlans}
        onViewPlans={openPlans}
      />

      <PaymentMethodCard
        method={paymentMethod.data}
        loading={paymentMethod.loading}
        error={paymentMethod.error}
        onRetry={paymentMethod.refetch}
        onUpdate={openPlans}
      />

      <BillingHistoryCard
        invoices={invoices.data}
        loading={invoices.loading}
        error={invoices.error}
        onRetry={invoices.refetch}
        showAll={showAllInvoices}
        onToggleShowAll={() => setShowAllInvoices((v) => !v)}
        onView={setViewInvoice}
      />

      <CancellationCard
        subscription={subscription.data}
        loading={subscription.loading}
        onCancelled={refetchAll}
      />

      <div className="settings-policies-disclaimer">
        All subscriptions, payments, and invoices are governed by our{' '}
        <a href={getDocsUrl('/docs/pricing-terms')} target="_blank" rel="noopener noreferrer">
          Pricing Terms & Conditions
        </a>{' '}
        and{' '}
        <a href={getDocsUrl('/docs/refund-policy')} target="_blank" rel="noopener noreferrer">
          Refund Policy
        </a>
        . For further details on payment security and billing practices, please read our{' '}
        <a href={getDocsUrl('/docs/terms-of-service')} target="_blank" rel="noopener noreferrer">
          Terms of Service
        </a>{' '}
        or contact{' '}
        <a href={getDocsUrl('/docs/help-support')} target="_blank" rel="noopener noreferrer">
          Help & Support
        </a>
        .
      </div>

      <InvoiceModal invoice={viewInvoice} onClose={() => setViewInvoice(null)} />
    </div>
  );
}

// ── Card 1: Plan overview ───────────────────────────────────────────────────

function PlanOverviewCard(props: {
  subscription: Subscription | null | undefined;
  subLoading: boolean;
  subError: string | null;
  onRetrySub(): void;
  onAdjustPlan(): void;
  onViewPlans(): void;
}) {
  const { subscription, subLoading, subError, onRetrySub, onAdjustPlan, onViewPlans } = props;
  const plan = subscription?.plan ?? null;

  const badge =
    plan != null ? (
      <span className={`settings-tier-badge tier-${plan.tier}`}>{plan.tier}</span>
    ) : undefined;

  return (
    <BillingCard
      title="Plan"
      headerRight={badge}
      loading={subLoading}
      error={subError}
      onRetry={onRetrySub}
      skeletonRows={4}
    >
      {plan == null ? (
        <div className="billing-empty">
          <p className="settings-muted">You don't have an active plan.</p>
          <button type="button" className="billing-btn billing-btn--primary" onClick={onViewPlans}>
            View plans
          </button>
        </div>
      ) : (
        <>
          <div className="billing-plan__top">
            <div>
              <div className="billing-plan__name">{plan.name}</div>
              <div className="billing-plan__price">{priceLabel(plan)}</div>
            </div>
            <div className="billing-plan__renewal">
              <span className="billing-kv__label">Next renewal</span>
              <span className="billing-kv__value">{renewalDate(subscription?.endDate)}</span>
            </div>
          </div>

          <div className="billing-actions">
            <button type="button" className="billing-btn billing-btn--primary" onClick={onAdjustPlan}>
              Adjust plan
            </button>
          </div>
        </>
      )}
    </BillingCard>
  );
}

// ── Card 2: Payment method ──────────────────────────────────────────────────

function PaymentMethodCard(props: {
  method: PaymentMethod | null | undefined;
  loading: boolean;
  error: string | null;
  onRetry(): void;
  onUpdate(): void;
}) {
  const { method, loading, error, onRetry, onUpdate } = props;
  const available = method?.available === true;

  if (available && method?.method === 'netbanking') {
    return null;
  }

  return (
    <BillingCard title="Payment method" loading={loading} error={error} onRetry={onRetry} skeletonRows={2}>
      {!available ? (
        <div className="billing-empty">
          <p className="settings-muted">No payment method on file.</p>
          <button type="button" className="billing-btn billing-btn--ghost" onClick={onUpdate}>
            Add a card
          </button>
        </div>
      ) : (
        <div className="billing-pay">
          <div className="billing-pay__info">
            <span className="billing-pay__icon" aria-hidden="true">
              ▦
            </span>
            <div>
              <div className="billing-pay__line">
                {method?.cardLast4
                  ? `${cardBrandLabel(method.cardBrand)} •••• ${method.cardLast4}`
                  : methodLabel(method?.method)}
              </div>
              {method?.expiryMonth != null && method?.expiryYear != null && (
                <div className="settings-muted billing-pay__exp">
                  Expires {String(method.expiryMonth).padStart(2, '0')}/{method.expiryYear}
                </div>
              )}
            </div>
          </div>
          <button type="button" className="billing-btn billing-btn--ghost" onClick={onUpdate}>
            Update card
          </button>
        </div>
      )}
    </BillingCard>
  );
}

function cardBrandLabel(brand: string | null | undefined): string {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}
function methodLabel(method: string | null | undefined): string {
  if (!method) return 'Card on file';
  return method.toUpperCase();
}

// ── Card 3: Billing history ─────────────────────────────────────────────────

function BillingHistoryCard(props: {
  invoices: Invoice[] | undefined;
  loading: boolean;
  error: string | null;
  onRetry(): void;
  showAll: boolean;
  onToggleShowAll(): void;
  onView(invoice: Invoice): void;
}) {
  const { invoices, loading, error, onRetry, showAll, onToggleShowAll, onView } = props;
  const all = invoices ?? [];
  const rows = showAll ? all : all.slice(0, HISTORY_PREVIEW);
  const hasMore = all.length > HISTORY_PREVIEW;

  return (
    <BillingCard title="Billing history" loading={loading} error={error} onRetry={onRetry} skeletonRows={4}>
      {all.length === 0 ? (
        <p className="settings-muted">No billing history.</p>
      ) : (
        <>
          <div className="billing-table-wrap">
            <table className="billing-table">
              <caption className="billing-sr-only">Recent invoices. {TIMEZONE_NOTE}.</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="billing-sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => (
                  <tr key={inv.id}>
                    <td>{formatDate(inv.createdAt)}</td>
                    <td>{formatMoney(inv.amount, inv.currency)}</td>
                    <td>
                      <span className={`billing-status billing-status--${inv.status.toLowerCase()}`}>
                        {STATUS_LABEL[inv.status] ?? inv.status}
                      </span>
                    </td>
                    <td className="billing-table__action">
                      <button
                        type="button"
                        className="billing-link-btn"
                        onClick={() => onView(inv)}
                        aria-label={`View invoice from ${formatDate(inv.createdAt)}`}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="billing-link-btn billing-icon-btn"
                        onClick={() => downloadInvoice(inv)}
                        aria-label={`Download invoice from ${formatDate(inv.createdAt)}`}
                        title="Download invoice"
                      >
                        <DownloadIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <button type="button" className="billing-link-btn billing-seeall" onClick={onToggleShowAll}>
              {showAll ? 'Show fewer' : `See all invoices (${all.length}) →`}
            </button>
          )}
        </>
      )}
    </BillingCard>
  );
}

// ── Card 5: Cancellation (danger zone) ──────────────────────────────────────

function CancellationCard(props: {
  subscription: Subscription | null | undefined;
  loading: boolean;
  onCancelled(): void;
}) {
  const { subscription, loading, onCancelled } = props;
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const hasActive = subscription != null;
  const disabled = loading || !hasActive || done;

  async function confirmCancel(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // POST takes no body — userId comes from the JWT server-side.
      await api.post('/api/v2/subscriptions/cancel', {});
      setDone(true);
      setConfirming(false);
      onCancelled();
    } catch (e) {
      setError((e as Error)?.message || 'Could not cancel your subscription');
    } finally {
      setBusy(false);
    }
  }

  return (
    <BillingCard title="Cancel plan" tone="danger" loading={loading} skeletonRows={2}>
      {done ? (
        <p className="settings-muted">Your subscription has been cancelled.</p>
      ) : !hasActive ? (
        <p className="settings-muted">You don't have an active subscription to cancel.</p>
      ) : (
        <>
          <p className="billing-danger__text">
            Your subscription will end on <strong>{formatDate(subscription?.endDate)}</strong>. You'll keep access
            until then.
          </p>
          {error && (
            <div className="billing-inline-error" role="alert">
              <span>{error}</span>
            </div>
          )}
          {confirming ? (
            <div className="billing-actions">
              <button
                type="button"
                className="billing-btn billing-btn--danger"
                disabled={busy}
                onClick={() => void confirmCancel()}
              >
                {busy ? 'Cancelling…' : 'Confirm cancellation'}
              </button>
              <button
                type="button"
                className="billing-btn billing-btn--ghost"
                disabled={busy}
                onClick={() => setConfirming(false)}
              >
                Keep plan
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="billing-btn billing-btn--danger"
              disabled={disabled}
              onClick={() => setConfirming(true)}
            >
              Cancel plan
            </button>
          )}
        </>
      )}
    </BillingCard>
  );
}
